// Runs in browsers and React Native: no Node imports (entry-graph.test.ts walks this package).
import {
  authContracts,
  type Contract,
  type PathParams,
  type Problem,
  type ProblemIssue,
} from '@bloom/contracts';
import type { z } from 'zod';
import { ApiError, ContractError } from './errors';

export interface TokenPair {
  readonly accessToken: string;
  readonly refreshToken: string;
  readonly accessTokenExpiresAt?: string;
  readonly refreshTokenExpiresAt?: string;
}

/** Where a bearer client keeps its tokens (the Expo app implements it with SecureStore). */
export interface TokenStore {
  get(): TokenPair | null | undefined | Promise<TokenPair | null | undefined>;
  set(pair: TokenPair): void | Promise<void>;
  clear(): void | Promise<void>;
}

export type ApiAuth =
  | {
      /** Session cookie (`credentials: 'include'`). */
      readonly mode: 'cookie';
      /** Double-submit CSRF: the readable cookie to echo in a header on unsafe methods. */
      readonly csrf?: { readonly cookie: string; readonly header?: string };
    }
  | { readonly mode: 'bearer'; readonly tokens: TokenStore };

export interface ApiClientConfig {
  /** API base including the version prefix, e.g. `https://api.example.com/v1`. A function is called per request. */
  readonly baseUrl: string | (() => string);
  readonly auth: ApiAuth;
  /** Called when a request ends as 401 (and 401 was not an expected answer): the session is over. */
  readonly onUnauthorized?: () => void;
  readonly fetch?: typeof fetch;
}

export interface CallOptions {
  readonly method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  readonly body?: unknown;
  readonly query?: Readonly<Record<string, string | number | boolean | null | undefined>>;
  /** Sent as X-Tenant-Id. Always explicit, never ambient. */
  readonly tenantId?: string | null;
  readonly signal?: AbortSignal;
  /** 401 is an expected answer here (session probe, sign-in): no expiry handling. */
  readonly allowUnauthenticated?: boolean;
}

export interface SchemaLike<T> {
  safeParse(data: unknown): { success: true; data: T } | { success: false; error: z.ZodError };
}

type CommonInput = Pick<CallOptions, 'tenantId' | 'signal' | 'allowUnauthenticated'>;

type ContractInput<C> =
  C extends Contract<infer P, infer B, infer Q, infer _R>
    ? CommonInput &
        ([PathParams<P>] extends [never]
          ? { params?: undefined }
          : { params: Record<PathParams<P>, string> }) &
        (B extends z.ZodType ? { body: z.input<B> } : { body?: undefined }) &
        (Q extends z.ZodType ? { query: z.input<Q> } : { query?: undefined })
    : never;

type ContractOutput<C> =
  C extends Contract<string, infer _B, infer _Q, infer R>
    ? R extends z.ZodType
      ? z.output<R>
      : undefined
    : never;

/** The input argument is optional only when every field of it is optional. */
type InputArg<I> =
  // biome-ignore lint/complexity/noBannedTypes: `{}` here means "an input with no required field"
  {} extends I ? [input?: I] : [input: I];

export interface ApiClient {
  /** Typed call: the response is validated with the contract's schema. */
  request<C extends Contract>(
    contract: C,
    ...input: InputArg<ContractInput<C>>
  ): Promise<ContractOutput<C>>;
  /** Untyped call for code that has no contract yet; an optional schema still validates the answer. */
  call<T = unknown>(path: string, options?: CallOptions & { schema?: SchemaLike<T> }): Promise<T>;
}

// The server always sends the expiry fields; a stored pair may lack them (older app versions).
const tokenPairSchema = authContracts.tokens.refresh.response.partial({
  accessTokenExpiresAt: true,
  refreshTokenExpiresAt: true,
});

const REFRESH_PATH = authContracts.tokens.refresh.path;
const TOKEN_EXPIRED = 'auth.token_expired';
const SAFE_METHODS = new Set(['GET', 'HEAD', 'OPTIONS']);

function newCorrelationId(): string | undefined {
  return (globalThis as { crypto?: { randomUUID?: () => string } }).crypto?.randomUUID?.();
}

function readCookie(name: string): string | undefined {
  const jar = (globalThis as { document?: { cookie?: string } }).document?.cookie ?? '';
  for (const part of jar.split(';')) {
    const [key, ...value] = part.trim().split('=');
    if (key === name) return decodeURIComponent(value.join('='));
  }
  return undefined;
}

function toUrl(base: string, path: string, query: CallOptions['query']): string {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value !== undefined && value !== null && value !== '') params.set(key, String(value));
  }
  const search = params.toString();
  return `${trimTrailingSlashes(base)}${path}${search ? `?${search}` : ''}`;
}

/** A loop, not `/\/+$/`: that regex is quadratic on many slashes before another character. */
export function trimTrailingSlashes(value: string): string {
  let end = value.length;
  while (end > 0 && value[end - 1] === '/') end--;
  return value.slice(0, end);
}

function fillPath(path: string, params: Record<string, string> | undefined): string {
  return path.replace(/:([A-Za-z0-9_]+)/g, (_, name: string) => {
    const value = params?.[name];
    if (value === undefined) throw new Error(`Missing path parameter "${name}" for ${path}`);
    return encodeURIComponent(value);
  });
}

async function toError(response: Response, correlationId: string | undefined): Promise<ApiError> {
  const isJson = /json/.test(response.headers.get('content-type') ?? '');
  const payload: unknown = isJson ? await response.json().catch(() => null) : null;
  const problem = (typeof payload === 'object' && payload !== null ? payload : {}) as Problem;
  return new ApiError({
    status: response.status,
    code: problem.code ?? `http_${response.status}`,
    detail: problem.detail ?? response.statusText,
    issues: (problem.errors as ProblemIssue[] | undefined) ?? [],
    correlationId: problem.correlationId ?? correlationId ?? null,
  });
}

export function createApiClient(config: ApiClientConfig): ApiClient {
  const { auth } = config;
  const doFetch: typeof fetch = (...args) => (config.fetch ?? fetch)(...args);
  const base = (): string =>
    typeof config.baseUrl === 'function' ? config.baseUrl() : config.baseUrl;

  const send = async (
    path: string,
    options: CallOptions,
    accessToken: string | undefined,
    correlationId: string | undefined,
  ): Promise<Response> => {
    const method = options.method ?? 'GET';
    const headers: Record<string, string> = { accept: 'application/json' };
    if (correlationId) headers['x-correlation-id'] = correlationId;
    if (options.body !== undefined) headers['content-type'] = 'application/json';
    if (options.tenantId) headers['x-tenant-id'] = options.tenantId;
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    if (auth.mode === 'cookie' && auth.csrf && !SAFE_METHODS.has(method)) {
      const token = readCookie(auth.csrf.cookie);
      if (token !== undefined) headers[auth.csrf.header ?? 'x-csrf-token'] = token;
    }
    try {
      return await doFetch(toUrl(base(), path, options.query), {
        method,
        headers,
        ...(auth.mode === 'cookie' ? { credentials: 'include' as const } : {}),
        body: options.body === undefined ? undefined : JSON.stringify(options.body),
        signal: options.signal,
      });
    } catch (error) {
      if ((error as Error)?.name === 'AbortError') throw error;
      throw new ApiError({
        status: 0,
        code: 'network',
        detail: 'Network request failed',
        correlationId,
      });
    }
  };

  // Bearer refresh, single-flight: concurrent 401s share one request.
  let refreshing: Promise<boolean> | undefined;

  const doRefresh = async (tokens: TokenStore, current: TokenPair): Promise<void> => {
    const correlationId = newCorrelationId();
    const response = await send(
      REFRESH_PATH,
      { method: 'POST', body: { refreshToken: current.refreshToken } },
      undefined,
      correlationId,
    );
    if (response.ok) {
      const parsed = tokenPairSchema.safeParse(await response.json().catch(() => null));
      if (parsed.success) {
        await tokens.set(parsed.data);
        return;
      }
    } else if (response.status >= 500 || response.status === 429 || response.status === 408) {
      // The server is down or busy, not the session: keep the tokens and let the caller retry.
      throw await toError(response, correlationId);
    }
    const failure = response.ok ? undefined : await toError(response, correlationId);
    await tokens.clear();
    throw new ApiError({
      status: 401,
      code: failure?.code ?? 'auth.refresh_failed',
      detail: failure?.message ?? 'Session refresh failed',
      issues: failure?.issues,
      correlationId: failure?.correlationId,
    });
  };

  /**
   * Refreshes unless another call already replaced the access token this call used. The promise is
   * created synchronously, so concurrent callers always join the same flight.
   */
  const refreshAfter401 = (tokens: TokenStore, usedAccessToken: string): Promise<boolean> => {
    refreshing ??= (async () => {
      const current = await tokens.get();
      if (!current) return false;
      if (current.accessToken !== usedAccessToken) return true; // already refreshed by another call
      await doRefresh(tokens, current);
      return true;
    })().finally(() => {
      refreshing = undefined;
    });
    return refreshing;
  };

  const execute = async (path: string, options: CallOptions): Promise<Response> => {
    const correlationId = newCorrelationId();
    const tokens = auth.mode === 'bearer' ? auth.tokens : undefined;
    const used = tokens ? (await tokens.get())?.accessToken : undefined;
    let response = await send(path, options, used, correlationId);
    if (tokens && used && response.status === 401) {
      const failure = await toError(response.clone(), correlationId);
      if (failure.code === TOKEN_EXPIRED) {
        let refreshed = false;
        try {
          refreshed = await refreshAfter401(tokens, used);
        } catch (error) {
          if (!options.allowUnauthenticated && error instanceof ApiError && error.status === 401) {
            config.onUnauthorized?.();
          }
          throw error;
        }
        if (refreshed) {
          response = await send(path, options, (await tokens.get())?.accessToken, correlationId);
        }
      }
    }
    return response;
  };

  const call = async <T>(
    path: string,
    options: CallOptions & { schema?: SchemaLike<T> } = {},
  ): Promise<T> => {
    const response = await execute(path, options);
    if (response.status === 204) return undefined as T;
    if (!response.ok) {
      const error = await toError(response, undefined);
      if (response.status === 401 && !options.allowUnauthenticated) config.onUnauthorized?.();
      throw error;
    }
    const isJson = /json/.test(response.headers.get('content-type') ?? '');
    const payload: unknown = isJson ? await response.json().catch(() => null) : null;
    if (!options.schema) return payload as T;
    const parsed = options.schema.safeParse(payload);
    if (parsed.success) return parsed.data;
    throw new ContractError(
      response.status,
      parsed.error.issues.map((issue) => ({
        path: issue.path.map(String).join('.'),
        message: issue.message,
        code: issue.code,
      })),
      response.headers.get('x-correlation-id'),
    );
  };

  return {
    call,
    async request(contract: Contract, input?: Record<string, unknown>) {
      const path = fillPath(contract.path, input?.params as Record<string, string> | undefined);
      const result = await call(path, {
        method: contract.method,
        body: input?.body,
        query: input?.query as CallOptions['query'],
        tenantId: input?.tenantId as string | null | undefined,
        signal: input?.signal as AbortSignal | undefined,
        allowUnauthenticated: input?.allowUnauthenticated as boolean | undefined,
        schema: contract.response ?? undefined,
      });
      return contract.response === null ? undefined : result;
    },
  } as ApiClient;
}
